import React, { useState } from "react";
import { useNavigate } from "react-router";
import { useAppSelector } from "../../hooks";
import CreateWatchPartyModal from "./CreateWatchPartyModal";
import LoginRequiredModal from "../Modals/LoginRequiredModal/LoginRequiredModal";
import { useGetWatchPartiesQuery, useJoinWatchPartyMutation, useLeaveWatchPartyMutation } from "../../services/api";

const defaultUserAvatar = "https://placehold.co/100x100/1f2937/ffffff/png?text=User";

const WatchPartyList: React.FC = () => {
  const navigate = useNavigate();
  const authUser = useAppSelector((state) => state.auth.user);
  
  const [filter, setFilter] = useState<"today" | "upcoming" | "all">("upcoming");
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(10);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  // RTK Query hooks
  const { data: parties = [], isLoading: loading, refetch } = useGetWatchPartiesQuery({
    filter,
    limit: 100,
    userId: authUser?.id,
  });

  const [joinParty] = useJoinWatchPartyMutation();
  const [leaveParty] = useLeaveWatchPartyMutation();

  const loadMore = () => {
    setVisibleCount((prev) => prev + 10);
  };

  const handleJoin = async (partyId: string) => {
    if (!authUser) return;

    try {
      await joinParty({ partyId, userId: authUser.id }).unwrap();
    } catch (err: any) {
      alert(err.message || "Failed to join watch party");
    }
  };

  const handleLeave = async (partyId: string) => {
    if (!authUser) return;

    try {
      await leaveParty({ partyId, userId: authUser.id }).unwrap();
    } catch (err: any) {
      alert(err.message || "Failed to leave watch party");
    }
  };

  // Format date/time
  const formatDateTime = (datetime: string, endTime?: string | null) => {
    if (endTime && new Date() > new Date(endTime)) return "Finished";

    const date = new Date(datetime);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();

    if (diffMs < 0) return "Started";

    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 60) return `In ${diffMinutes} min`;
    if (diffHours < 24) return `In ${diffHours} hours`;
    if (diffDays < 7) return `In ${diffDays} days`;

    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Filter parties by search
  const filteredParties = parties.filter(
    (party: any) =>
      party.title.toLowerCase().includes(searchQuery.toLowerCase()) || party.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const visibleParties = filteredParties.slice(0, visibleCount);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-white text-lg">Loading watch parties...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Watch Parties 🎉</h2>

        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          {/* Search Input */}
          <div className="relative flex-1 sm:min-w-[300px]">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search parties..."
              className="w-full px-4 py-2 pl-10 rounded-lg bg-gray-800 border border-gray-700 focus:border-red-600 focus:outline-none text-white placeholder-gray-500"
            />
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* Filter Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => setFilter("today")}
              className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                filter === "today" ? "bg-red-600 text-white" : "bg-gray-800 text-gray-400 hover:text-white"
              }`}
            >
              Today
            </button>
            <button
              onClick={() => setFilter("upcoming")}
              className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                filter === "upcoming" ? "bg-red-600 text-white" : "bg-gray-800 text-gray-400 hover:text-white"
              }`}
            >
              Upcoming
            </button>
            <button
              onClick={() => setFilter("all")}
              className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                filter === "all" ? "bg-red-600 text-white" : "bg-gray-800 text-gray-400 hover:text-white"
              }`}
            >
              All
            </button>
          </div>
        </div>
      </div>

      {/* Create Watch Party Button */}

      <div className="flex justify-end">
        <button
          onClick={() => {
            if (authUser) {
              setShowCreateModal(true);
            } else {
              setShowLoginModal(true);
            }
          }}
          className="px-4 py-2 rounded-lg font-semibold transition-colors text-white bg-red-600 hover:bg-red-700"
        >
          Create Watch Party
        </button>
      </div>

      {/* Parties List */}
      {filteredParties.length === 0 ? (
        <div className="text-center text-gray-500 py-16">
          <p className="text-lg mb-2">{searchQuery ? "No parties match your search" : "No watch parties yet"}</p>
          {!searchQuery && <p className="text-sm">Create one from any film chat room!</p>}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visibleParties.map((party: any) => (
              <div
                key={party.id}
                className="bg-gray-900 border-2 border-red-600 rounded-xl overflow-hidden hover:shadow-[0_0_20px_rgba(220,38,38,0.4)] transition-shadow"
                onClick={() => navigate(`/watch-party/${party.id}`)}
              >
                {(() => {
                  const isFinished = party.end_time && new Date() > new Date(party.end_time);
                  return (
                    <>
                {/* Film Thumbnail */}
                <div
                  className="h-48 bg-cover bg-center relative cursor-pointer bg-gray-800"
                  style={{
                    backgroundImage: party.film?.primary_image ? `url(${party.film.primary_image})` : "none",
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent"></div>
                  <div className="absolute bottom-2 left-2 right-2">
                    <h3 className="text-white font-bold text-lg line-clamp-2 flex items-center gap-2">
                      {party.title}
                      {party.is_private && <span title="Private Party">🔒</span>}
                    </h3>
                    <p className="text-gray-300 text-sm">{party.type === "tvSeries" ? "TV Series" : "Movie"}</p>
                  </div>
                </div>

                {/* Party Info */}
                <div className="p-4 space-y-3">
                  {/* Host */}
                  <div className="flex items-center gap-2">
                    {(() => {
                      const avatarSrc =
                        party.host_profile?.avatar_url && party.host_profile.avatar_url.trim().length > 0
                          ? party.host_profile.avatar_url
                          : defaultUserAvatar;

                      return (
                        <img
                          key={avatarSrc}
                          src={avatarSrc}
                          alt={party.host_profile?.display_name}
                          className="w-8 h-8 rounded-full border-2 border-red-600"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = defaultUserAvatar;
                          }}
                        />
                      );
                    })()}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-400">Hosted by</p>
                      <p className="text-white font-semibold truncate">{party.host_profile?.display_name || "Unknown"}</p>
                    </div>
                  </div>

                  {/* Time */}
                  <div className="flex items-center gap-2 text-cyan-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-semibold">{formatDateTime(party.scheduled_time, party.end_time)}</span>
                  </div>

                  {/* Description */}
                  {party.description && <p className="text-sm text-gray-400 line-clamp-2">{party.description}</p>}

                  {/* Participants */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">
                      {party.participants_count} / {party.max_participants} joined
                    </span>
                    {isFinished ? (
                      <span className="px-2 py-1 bg-gray-700 text-gray-400 rounded text-xs font-semibold">Finished</span>
                    ) : party.status === "upcoming" ? (
                      <span className="px-2 py-1 bg-green-600/20 text-green-400 rounded text-xs font-semibold">Upcoming</span>
                    ) : null}
                  </div>

                  {/* Action Button */}
                  {authUser && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isFinished) (party.is_joined ? handleLeave(party.id) : handleJoin(party.id));
                      }}
                      disabled={isFinished || (!party.is_joined && (party.participants_count! >= party.max_participants || party.is_private))}
                      className={`w-full py-2 rounded-lg font-semibold transition-colors ${
                        isFinished
                          ? "bg-gray-800 text-gray-500 cursor-not-allowed"
                          : party.is_joined
                          ? "bg-gray-700 hover:bg-gray-600 text-white"
                          : party.is_private
                          ? "bg-gray-800 text-gray-500 cursor-not-allowed border border-gray-700"
                          : party.participants_count! >= party.max_participants
                          ? "bg-gray-800 text-gray-600 cursor-not-allowed"
                          : "bg-red-600 hover:bg-red-700 text-white"
                      }`}
                    >
                      {isFinished ? "Ended" : party.is_joined ? "Leave Party" : party.is_private ? "Private Party 🔒" : party.participants_count! >= party.max_participants ? "Full" : "Join Party"}
                    </button>
                  )}
                </div>
                </>
                  );
                })()}
              </div>
            ))}
          </div>

          {filteredParties.length > visibleCount && (
            <div className="flex justify-center mt-6">
              <button
                onClick={loadMore}
                className="px-6 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg border border-red-600 font-semibold transition-colors"
              >
                Show More ({filteredParties.length - visibleCount} remaining)
              </button>
            </div>
          )}
        </>
      )}

      {showCreateModal && authUser && (
        <CreateWatchPartyModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            refetch();
            setVisibleCount(10);
          }}
        />
      )}

      <LoginRequiredModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
    </div>
  );
};

export default WatchPartyList;
